import axios from 'axios'
import {useCallback} from 'react'
import {useGetConfig} from 'src/common/hooks/useGetConfig'

export type LeaderboardEntry = {
  username: string
  score: number
  position: number
}

export type LeaderboardData = {
  topTenPlayers: LeaderboardEntry[]
  totalPlayers: number
  currentPlayer: LeaderboardEntry
}

export const useFraudGameApi = () => {
  const config = useGetConfig()

  const startSession = useCallback(
    async (params: {playerId?: string; referrerId?: string | null; fingerprint?: string}) => {
      const {data} = await axios.post<{excludedQuestionIds: string[]}>(
        `${config.API_URL}/v1/fraud-game/session/start`,
        {playerId: params.playerId, referrerId: params.referrerId, fingerprint: params.fingerprint},
      )

      return data
    },
    [config.API_URL],
  )

  const createPlayer = useCallback(
    async (params: {
      id: string
      email: string
      username: string
      score: number
      questionIds: string[]
      captchaToken?: string | null
    }) => {
      const {captchaToken, ...body} = params
      const headers = captchaToken ? {'X-Captcha-Token': captchaToken} : undefined

      const {data} = await axios.post<{playerId: string; replayed: boolean; username: string}>(
        `${config.API_URL}/v1/fraud-game/player`,
        body,
        {headers},
      )

      return data
    },
    [config.API_URL],
  )

  const getLeaderboard = useCallback(
    async (playerId: string): Promise<LeaderboardData> => {
      const {data} = await axios.get<LeaderboardData>(`${config.API_URL}/v1/fraud-game/leaderboard`, {
        params: {playerId, type: 'GLOBAL'},
      })

      return data
    },
    [config.API_URL],
  )

  const createReferral = useCallback(
    async (params: {referredEmail: string; refererId: string}) => {
      await axios.post(`${config.API_URL}/v1/fraud-game/referral`, params)
    },
    [config.API_URL],
  )

  const logGame = useCallback(
    async (params: {playerId: string; score: number; fingerprint?: string}) => {
      await axios.post(`${config.API_URL}/v1/fraud-game/game/log`, {
        playerId: params.playerId,
        score: params.score,
        fingerprint: params.fingerprint,
      })
    },
    [config.API_URL],
  )

  return {startSession, createPlayer, getLeaderboard, createReferral, logGame}
}
